import { useState } from 'react';
import { XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';

/**
 * „Problem / Idee melden" — 1:1 aus powerCONTACT (IssueModal.tsx), Christian 04.09.
 * Erstellt ein Gitea-Issue (christian/powercare) ueber das Backend (/api/issues),
 * Typ + Titel + Beschreibung, optional Screenshot. Farben: primary (Mandanten-Farbe).
 */
type IssueType = 'bug' | 'feature' | 'improvement';

const TYPES: { id: IssueType; label: string; icon: string }[] = [
  { id: 'bug', label: 'Fehler', icon: '🐞' },
  { id: 'feature', label: 'Wunsch', icon: '💡' },
  { id: 'improvement', label: 'Verbesserung', icon: '✨' },
];

export default function IssueModal({ onClose }: { onClose: () => void }) {
  const [type, setType] = useState<IssueType>('bug');
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState<{ iid: number; web_url: string } | null>(null);

  const submit = async () => {
    if (!title.trim() || !description.trim()) { setError('Bitte Titel und Beschreibung ausfüllen.'); return; }
    setBusy(true); setError('');
    try {
      let desc = description.trim();
      if (file) {
        const fd = new FormData(); fd.append('file', file);
        const up = await fetch('/api/issues/upload', { method: 'POST', body: fd, credentials: 'include' });
        if (up.ok) { const u = await up.json(); if (u.markdown) desc += `\n\n${u.markdown}`; }
      }
      const res = await fetch('/api/issues', {
        method: 'POST', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ type, title: title.trim(), description: desc }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Fehler beim Melden'); }
      setDone(await res.json());
    } catch (e) { setError(e instanceof Error ? e.message : 'Unbekannter Fehler'); }
    finally { setBusy(false); }
  };

  return (
    // Kein Schliessen per Backdrop-Klick: getippter Text bliebe sonst nicht erhalten — nur X / Abbrechen / Senden.
    <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto bg-black/40 p-4">
      <div className="my-8 max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-ps-dark-100 p-4">
          <h3 className="text-lg font-medium text-ps-dark-800">Problem / Idee melden</h3>
          <button onClick={onClose} className="rounded p-1 text-ps-dark-400 hover:bg-ps-dark-100"><XMarkIcon className="h-5 w-5" /></button>
        </div>
        {done ? (
          <div className="space-y-3 p-6 text-center">
            <CheckCircleIcon className="mx-auto h-12 w-12 text-emerald-500" />
            <p className="text-ps-dark-700">Danke! Gemeldet als <strong>#{done.iid}</strong>.</p>
            <a href={done.web_url} target="_blank" rel="noopener noreferrer" className="inline-block text-sm text-primary-600 hover:underline">In Gitea ansehen ↗</a>
            <div><button onClick={onClose} className="mt-2 rounded-md bg-ps-dark-600 px-4 py-2 text-sm text-white hover:bg-ps-dark-700">Schliessen</button></div>
          </div>
        ) : (
          <div className="space-y-4 p-4">
            <div className="flex gap-2">
              {TYPES.map((t) => (
                <button key={t.id} onClick={() => setType(t.id)}
                  className={`flex-1 rounded-md border px-3 py-2 text-sm transition ${type === t.id ? 'border-primary-400 bg-primary-50 text-primary-700' : 'border-ps-dark-200 text-ps-dark-600 hover:bg-ps-dark-50'}`}>
                  {t.icon} {t.label}
                </button>
              ))}
            </div>
            <input type="text" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Kurzer Titel"
              className="w-full rounded-md border border-ps-dark-200 px-3 py-2 text-sm focus:border-primary-300 focus:outline-none" />
            <textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={5}
              placeholder="Was ist passiert? Was hast du erwartet? (Bei Fehlern: Schritte zum Reproduzieren)"
              className="w-full rounded-md border border-ps-dark-200 px-3 py-2 text-sm focus:border-primary-300 focus:outline-none" />
            <div>
              <label className="text-xs text-ps-dark-500">Screenshot (optional) — Achtung: kann Patientendaten zeigen, bleibt in der App gespeichert</label>
              <input type="file" accept="image/*" onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mt-1 block w-full text-xs text-ps-dark-600 file:mr-3 file:rounded-md file:border-0 file:bg-ps-dark-100 file:px-3 file:py-1.5 file:text-ps-dark-700 hover:file:bg-ps-dark-200" />
            </div>
            {error && <p className="text-sm text-red-600">{error}</p>}
            <div className="flex justify-end gap-2">
              <button onClick={onClose} className="rounded-md px-4 py-2 text-sm text-ps-dark-600 hover:bg-ps-dark-100">Abbrechen</button>
              <button onClick={submit} disabled={busy} className="rounded-md bg-primary-600 px-4 py-2 text-sm text-white hover:bg-primary-700 disabled:opacity-60">
                {busy ? 'Senden…' : 'Absenden'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
